<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';

import ConnectFlowService from '@/services/api/ConnectFlowService';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    ConnectFlowActions,
    ConnectFlowGetters,
    ConnectFlowModel,
    ConnectFlowFilter,
    ConnectFlowServiceGetAllParams,
    FilterItem,
    SortMenuItem,
    Permission,
} from '@/types';

import {
    ConnectFlowListItem,
    ConnectFlowListSort,
    ConnectFlowListFilters,
} from '@/views/pages/connect-flow/components';

import ConnectFlowIcon from '@/components/svg/ConnectFlowIcon';

const route = useRoute();
const router = useRouter();

const filterStore = useFilterStore();
const uiStore = useUiStore();

const connectFlows = ref<ConnectFlowModel[]>([]);
const count = ref(0);
const perPage = 10;

const currentPage = computed({
    get: () => store.getters[ConnectFlowGetters.PAGE],
    set: (value: number) => store.dispatch(ConnectFlowActions.SET_PAGE, value),
});

const search = computed({
    get: () => store.getters[ConnectFlowGetters.SEARCH],
    set: (value: string) => store.dispatch(ConnectFlowActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[ConnectFlowGetters.SORT],
    set: (value: SortMenuItem[]) => store.dispatch(ConnectFlowActions.SET_SORT, value),
});

const levelFilter = computed(() => filterStore.level);
const filter: ConnectFlowFilter = store.getters[ConnectFlowGetters.FILTER];

const activeFilters = computed<FilterItem[]>(
    () => store.getters[ConnectFlowGetters.ACTIVE_FILTERS],
);

const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getParams = (): ConnectFlowServiceGetAllParams => ({
    page: currentPage.value,
    per_page: perPage,
    order_by: orderBy.value,
    is_active: filter.isActive !== null ? (filter.isActive.value as boolean) : undefined,
    search: search.value,
});

const fetchConnectFlows = async () => {
    await setLoading(true);
    try {
        const { data, headers } = await ConnectFlowService.getAll<ConnectFlowModel[]>({
            params: getParams(),
        });
        connectFlows.value = data;
        count.value = parseInt(headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const setIsActive = async (item: ConnectFlowModel, isActive: boolean) => {
    await setLoading(true);
    try {
        const { data } = await (
            isActive
                ? (id: number) => ConnectFlowService.activate(id)
                : (id: number) => ConnectFlowService.inactivate(id)
        )(item.id);
        toast.success(data.message);
        await fetchConnectFlows();
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const handleActivateClick = (item: ConnectFlowModel) => setIsActive(item, true);
const handleInactivateClick = (item: ConnectFlowModel) => setIsActive(item, false);

const handleRemoveSortClick = (item: SortMenuItem) => {
    const index = sortMenuSelected.value.indexOf(item);
    if (index > -1) sortMenuSelected.value.splice(index, 1);
};

const handleRemoveFilterClick = (item: FilterItem) => {
    store.dispatch(ConnectFlowActions.CLEAR_FILTER, item);
};

watch([currentPage], () => {
    fetchConnectFlows();
});

watch([levelFilter, search, filter, orderBy], () => {
    if (currentPage.value === 1) fetchConnectFlows();
    else currentPage.value = 1;
});

if (route.query.clearStateOnSetup) {
    search.value = '';
    sortMenuSelected.value = [];
    currentPage.value = 1;
    router.replace({ query: {} });
}

onMounted(fetchConnectFlows);
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex items-center text-xl font-semibold text-gray-800">
                    <ConnectFlowIcon class="mr-1 h-6 w-6 text-gray-500" aria-hidden="true" />
                    {{ $t('core.entity.ConnectFlows') }}
                </h1>
            </template>
            <template #actions>
                <router-link
                    v-if="$can(Permission.CREATE_CONNECT_FLOW)"
                    :to="{ name: 'CreateConnectFlow' }"
                >
                    <UiButton variant="primary" text-variant="white">
                        {{ $t('core.actions.Add') }}
                    </UiButton>
                </router-link>
            </template>
        </UiPageHeader>

        <div class="space-y-2">
            <UiPanel class="flex px-4 py-2" no-padding>
                <UiListSearch
                    v-model="search"
                    :placeholder="$t('connectFlow.filters.search')"
                    class="flex-1"
                />
                <div class="flex items-center space-x-4">
                    <ConnectFlowListSort v-model="sortMenuSelected" />
                    <ConnectFlowListFilters />
                </div>
            </UiPanel>

            <UiPanel v-if="sortMenuSelected.length || activeFilters.length" class="space-x-2">
                <UiSortMenuBadge
                    v-for="selected in sortMenuSelected"
                    :key="selected.key"
                    :item="selected"
                    @remove="handleRemoveSortClick(selected)"
                />
                <UiBadge
                    v-for="item in activeFilters"
                    :key="`activeFilter${item.field}`"
                    removable
                    @remove="handleRemoveFilterClick(item)"
                >
                    {{ $t(`connectFlow.filters.${item.field}.label`) }}: {{ item.label }}
                </UiBadge>
            </UiPanel>

            <div v-if="connectFlows.length" class="space-y-2">
                <ConnectFlowListItem
                    v-for="connectFlow in connectFlows"
                    :key="connectFlow.id"
                    :model-value="connectFlow"
                    @activate="handleActivateClick"
                    @inactivate="handleInactivateClick"
                />
            </div>
            <UiPanel v-else class="flex items-center justify-center">
                {{ $t('core.messages.noRecordsFound') }}
            </UiPanel>

            <UiPagination
                v-if="connectFlows.length"
                v-model="currentPage"
                :count="count"
                :perPage="perPage"
            />
        </div>
    </div>
</template>
